// Pure trilinear sampling on a uniform Cartesian grid. No THREE deps.
//
// Grid layout (matches the HDF5 conventions used by data-processing/):
//   index = i * (ny * nz) + j * nz + k   with i:x, j:y, k:z
//   physical(i,j,k) = (xmin + i*dx, ymin + j*dy, zmin + k*dz)

import type {
  FieldComponents,
  MutableVec3,
  PhysicsGrid,
  ScalarField,
  Vec3,
  VectorSampler,
} from './types';

export function isOutsideBounds(grid: PhysicsGrid, x: number, y: number, z: number): boolean {
  return (
    x < grid.xmin || x > grid.xmax ||
    y < grid.ymin || y > grid.ymax ||
    z < grid.zmin || z > grid.zmax
  );
}

export function trilinear(values: ScalarField, grid: PhysicsGrid, x: number, y: number, z: number): number {
  const { nx, ny, nz, xmin, ymin, zmin, dx, dy, dz } = grid;
  const fx = (x - xmin) / dx;
  const fy = (y - ymin) / dy;
  const fz = (z - zmin) / dz;
  const i0 = Math.floor(fx), j0 = Math.floor(fy), k0 = Math.floor(fz);
  const tx = fx - i0, ty = fy - j0, tz = fz - k0;
  const i1 = i0 + 1, j1 = j0 + 1, k1 = k0 + 1;
  if (i0 < 0 || j0 < 0 || k0 < 0 || i1 >= nx || j1 >= ny || k1 >= nz) return 0;
  const stride = ny * nz;
  const idx = (ii: number, jj: number, kk: number): number => ii * stride + jj * nz + kk;
  const c000 = values[idx(i0, j0, k0)]!, c100 = values[idx(i1, j0, k0)]!;
  const c010 = values[idx(i0, j1, k0)]!, c110 = values[idx(i1, j1, k0)]!;
  const c001 = values[idx(i0, j0, k1)]!, c101 = values[idx(i1, j0, k1)]!;
  const c011 = values[idx(i0, j1, k1)]!, c111 = values[idx(i1, j1, k1)]!;
  const c00 = c000 + (c100 - c000) * tx;
  const c10 = c010 + (c110 - c010) * tx;
  const c01 = c001 + (c101 - c001) * tx;
  const c11 = c011 + (c111 - c011) * tx;
  const c0 = c00 + (c10 - c00) * ty;
  const c1 = c01 + (c11 - c01) * ty;
  return c0 + (c1 - c0) * tz;
}

// Build a vector sampler bound to a fixed grid + component arrays.
export function makeVectorSampler(grid: PhysicsGrid, components: FieldComponents): VectorSampler {
  const { Bx, By, Bz } = components;
  return function sample(x: number, y: number, z: number): MutableVec3 {
    if (!Bx || !By || !Bz) return [0, 0, 0];
    if (isOutsideBounds(grid, x, y, z)) return [0, 0, 0];
    return [
      trilinear(Bx, grid, x, y, z),
      trilinear(By, grid, x, y, z),
      trilinear(Bz, grid, x, y, z),
    ];
  };
}

export function vecMag(v: Vec3): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

export function vecNorm(v: Vec3): MutableVec3 {
  const m = vecMag(v);
  if (m === 0) return [0, 0, 0];
  return [v[0] / m, v[1] / m, v[2] / m];
}
